import get from 'lodash/get';
import {
  call,
  put,
  select,
  takeLatest,
  cancel,
  take,
} from 'redux-saga/effects';
import { ROOM_INVITATION_TOKEN } from '../../../constants/storageKeys';

import { virtualVisitActions } from '../virtual-visit';
import {
  COMPLETED_APPOINTMENT_STATUSES,
  VIRTUAL_VISIT_INVITE_TYPE,
} from '../../../constants/index';
import * as roomInvitationJoin from '../requests/room-invitation-join-req';
import * as roomInvitationInfo from '../requests/room-invitation-info-req';
import * as connectionRoomJoin from '../requests/connection-room-join-req';
import * as joinByVisitCode from '../requests/join-by-visit-code-req';
import {
  selectRoomInvitationJoinData,
  selectRoomInvitationJoinStatus,
  selectConnectionRoomJoinData,
  selectVirtualRoomByCodeData,
} from '../selectors';
import { APPOINTMENT_DETAILS_TYPES } from '../appointment-details';
import { checkPermission } from './checkPermission';

/**
 * Handle join to virtual room by link
 * as PATIENT or GUEST (Invite Call)
 * */
function* roomInvitationJoinSuccessRequest({ payload: meta }) {
  const data = yield select(selectRoomInvitationJoinData);
  const status = yield select(selectRoomInvitationJoinStatus);
  const roomName = get(data, 'patientUserInfo.displayableName', '');
  const roomNumber = `VV-${get(data, 'id', '')}`;
  const visitTypeName = get(data, 'visitType.name', '');

  const completed = COMPLETED_APPOINTMENT_STATUSES.includes(status);
  const consents = get(data, 'consentsAssignment.consents') || [];
  const allSigned = consents.every(consent => consent.accepted);
  const isPatientInvitatin = Boolean(get(meta, 'patientProfile'));
  const shouldSignConsent = !allSigned && isPatientInvitatin;
  const type = isPatientInvitatin
    ? VIRTUAL_VISIT_INVITE_TYPE.PATIENT
    : VIRTUAL_VISIT_INVITE_TYPE.GUEST;

  if (!completed) {
    const config = {
      ...meta,
      type,
      roomName,
      roomNumber,
      visitTypeName,
      shouldSignConsent,
      appointmentId: get(data, 'id'),
    };

    yield call(() => checkPermission(true));
    yield put(virtualVisitActions.setConfig(config));

    if (shouldSignConsent) yield cancel();
    yield put(virtualVisitActions.setVisibility(true));
  }
}

/**
 * Handle join to virtual room by link
 * as GUEST (Direct Call)
 * */
function* handleConnectionRoomJoin() {
  const connectionRoomData = yield select(selectConnectionRoomJoinData);
  const config = {
    roomNumber: get(connectionRoomData, 'chatRoomId', ''),
    room: get(connectionRoomData, 'credentials.room', ''),
    token: get(connectionRoomData, 'credentials.token', ''),
    apiKey: get(connectionRoomData, 'credentials.apiKey', ''),
    connectionRoom: true,
    type: VIRTUAL_VISIT_INVITE_TYPE.CONNECTION_ROOM,
  };
  yield call(() => checkPermission(true));
  yield put(virtualVisitActions.setConfig(config));
  yield put(virtualVisitActions.setVisibility(true));
}

/**
 * Handle join to virtual room by visit code
 * as PATIENT (in office visit)
 * */
function* initiateJoinVisitByCode({ payload: visitCode }) {
  yield put(joinByVisitCode.actions.send({ visitCode }));
  const { payload: meta } = yield take(joinByVisitCode.consts.ADD_META);
  const data = yield select(selectVirtualRoomByCodeData);
  const consents = get(data, 'consentsAssignment.consents') || [];
  const allSigned = consents.every(consent => consent.accepted);
  const roomName = get(data, 'patientUserInfo.displayableName', '');
  const roomNumber = `VV-${get(data, 'id', '')}`;
  const shouldSignConsent = !allSigned;

  const config = {
    ...meta,
    roomNumber,
    roomName,
    shouldSignConsent,
    appointmentId: get(data, 'id'),
    type: VIRTUAL_VISIT_INVITE_TYPE.IN_OFFICE,
  };

  yield call(() => checkPermission(true));
  yield put(virtualVisitActions.setConfig(config));

  if (shouldSignConsent) yield cancel();
  yield put(virtualVisitActions.setVisibility(true));
}

function* handleRoomInvitationInfoSuccess({ payload: meta }) {
  const isRequiredPayment = get(meta, 'isRequiredPayment', false);
  const inviteCode = get(window.location, 'hash', '').slice(1);

  if (!isRequiredPayment) {
    yield put(roomInvitationJoin.actions.send({ inviteCode }));
    return;
  }

  window.storage.setItem(ROOM_INVITATION_TOKEN, inviteCode);

  yield take(APPOINTMENT_DETAILS_TYPES.PAYMENT_SUCCESS);

  window.storage.removeItem(ROOM_INVITATION_TOKEN);
  yield put(roomInvitationJoin.actions.send({ inviteCode }));
}

export default function* roomInvitationJoinSaga() {
  yield takeLatest(
    [roomInvitationInfo.consts.ADD_META],
    handleRoomInvitationInfoSuccess,
  );

  yield takeLatest(
    [roomInvitationJoin.consts.ADD_META],
    roomInvitationJoinSuccessRequest,
  );

  yield takeLatest(
    [connectionRoomJoin.consts.SUCCEED],
    handleConnectionRoomJoin,
  );
  yield takeLatest([joinByVisitCode.consts.INIT], initiateJoinVisitByCode);
}



// WEBPACK FOOTER //
// ./src/redux/healthboard/sagas/room-invitation-join-saga.js